"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.requestPayloadProvider = exports.unsafePayloadValidator = exports.getRequestBody = exports.getHeader = void 0;
const assertions_1 = require("../assertions");
const errors_1 = require("../errors");
const guards_1 = require("../guards");
/**
 * Get the value of a header, case insensitive; note if there are multiple headers of the same
 * value, this only returns the first value
 *
 * @param headers
 * @param name the name of the header, case ignored
 */
const getHeader = (headers, name) => {
    const key = Object.keys(headers).find(header => header.toLowerCase() === name.toLowerCase());
    const value = key ? headers[key] : undefined;
    return value instanceof Array
        ? value[0]
        : value;
};
exports.getHeader = getHeader;
/**
 * Gets the body of a request
 * @param request
 * @throws InvalidRequestError if the content type is not application/json or not parsable
 */
const getRequestBody = (request) => {
    if ((0, exports.getHeader)(request.headers, 'content-type') !== 'application/json') {
        throw new errors_1.InvalidRequestError('unknown content type: ' + (0, exports.getHeader)(request.headers, 'content-type'));
    }
    if (!request.body) {
        return {};
    }
    try {
        return JSON.parse(request.body);
    }
    catch (error) {
        // Since the body is provided by the user, invalid JSON in the body is an invalid request
        // We return the message which tells them why the JSON is invalid, but no backtrace
        throw new errors_1.InvalidRequestError((0, assertions_1.assertErrorInstanceOf)(error, SyntaxError).message);
    }
};
exports.getRequestBody = getRequestBody;
/* utils and middleware for loading request payload (must follow this pattern for `PayloadForRoute` to work) */
/**
 * stub validator because writing validators is annoying
 */
const unsafePayloadValidator = () => (input) => {
    return (0, guards_1.isPlainObject)(input) && Object.keys(input).length > 0;
};
exports.unsafePayloadValidator = unsafePayloadValidator;
/**
 * Middleware that validates the request payload and adds it to the middleware (with key `payload`)
 * so it can be accessed downstream.
 *
 * This middleware accomplishes a few things:
 *   - establishes type of payload for route body logic
 *   - validates the payload for route logic
 *   - establishes type of payload for client logic calling this route
 *
 * @param validator a guard function that takes the request payload and returns true if it is valid,
 * narrowing the type of the payload to the correct type
 * @throws InvalidRequestError if the payload is not valid
 *
 * @example
 * export const exampleRoute = createRoute({
 *   name: 'exampleRoute', method: METHOD.POST, path: '/example/:id',
 *   requestServiceProvider: composeServiceMiddleware(
 *     // here we are adding to previously composed middleware
 *     requestServiceProvider,
 *     requestPayloadProvider(validatePayload)
 *   )},
 *   async(params: {id: string}, services) => {
 *     const result = await services.myDocumentStore.putItem({
 *       ...services.payload,
 *       id: params.id,
 *     });
 *     return apiJsonResponse(201, result);
 *   }
 * );
 */
const requestPayloadProvider = (validator) => () => (requestServices) => {
    const payload = (0, exports.getRequestBody)(requestServices.request);
    // for more precise error messages, throw your own InvalidRequestError from your validator function
    if (!validator(payload)) {
        throw new errors_1.InvalidRequestError();
    }
    return { ...requestServices, payload };
};
exports.requestPayloadProvider = requestPayloadProvider;
